import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MapSocketMessageEventType } from "./interface";
import { buildAlertEntityId } from "utils/AlertUtil";
import {
  BaseAlertEntity,
  DashboardFilterResult,
  DashboardAlertData,
  MapSearchData,
} from "api/interfaces/dashboardInterface.interface";
import {
  convertToMapSearchData,
  convertToMarkerGroupingData,
} from "utils/DashboardUtil";
import { RootState } from "redux/store";

enum WS_MAP_PAYLOAD_STATUS {
  active = "alerts",
  closed = "alerts",
  warning = "warnings",
}

type AlertListKey = "alerts" | "warnings";
type StatusName = keyof typeof WS_MAP_PAYLOAD_STATUS;

export interface AlertServerPayload extends BaseAlertEntity {
  event: MapSocketMessageEventType;
  bell: boolean;
}

interface AlertEntity extends DashboardAlertData {
  alertUniqueId: string;
}

interface StompMapState {
  originFilterSearchResponse: DashboardFilterResult[]; // API 원본 (불변 보존)
  currentData: DashboardFilterResult[]; // 소켓/필터 반영된 현재값
  visibleData: MapSearchData[];
  mapLastUpdatedAt: Date | null;
  needToForceUpdated: Date | null;
  mapLastUpdatedIdForAlertView: string | null;

  mapBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  searchAsMoved: boolean;
}

const initialState: StompMapState = {
  originFilterSearchResponse: [],
  currentData: [],
  visibleData: [],
  mapLastUpdatedAt: null,
  needToForceUpdated: null,
  mapLastUpdatedIdForAlertView: null,

  mapBounds: undefined,
  searchAsMoved: false,
};

const alertId = (alertEnt: BaseAlertEntity) =>
  buildAlertEntityId(alertEnt as unknown as AlertServerPayload);

const makeAlertEntityByWs = (payload: AlertServerPayload): AlertEntity => {
  return {
    ...payload,
    alertUniqueId: alertId(payload),
    status: payload.status.toLowerCase(),
  };
};

const allCloseAlerts = ({
  results,
  newAlert,
}: {
  results: DashboardFilterResult[];
  newAlert: AlertEntity;
}): DashboardFilterResult[] => {
  return results.map((item) =>
    item.recorderId === newAlert.recorderId ? { ...item, alerts: [] } : item
  );
};

const closeEventByPayload = ({
  results,
  newAlert,
  status,
}: {
  results: DashboardFilterResult[];
  newAlert: AlertEntity;
  status: WS_MAP_PAYLOAD_STATUS;
}): DashboardFilterResult[] => {
  const key = status as AlertListKey;

  return results.map((item) => {
    if (item.recorderId !== newAlert.recorderId) return item;

    const currentEvent = item[key] ?? [];

    return {
      ...item,
      [key]: currentEvent.filter((a) => alertId(a) !== newAlert.alertUniqueId),
    };
  });
};

const addEventByPayload = ({
  results,
  newAlert,
  status,
}: {
  results: DashboardFilterResult[];
  newAlert: AlertEntity;
  status: WS_MAP_PAYLOAD_STATUS;
}): DashboardFilterResult[] => {
  const key = status as AlertListKey;

  return (results ?? []).map((item) => {
    if (item.recorderId !== newAlert.recorderId) return item;

    const currentEvent = item[key] ?? [];
    const alertIndex = currentEvent.findIndex(
      (a) => alertId(a) === newAlert.alertUniqueId
    );

    let updatedEvent;
    if (alertIndex === -1) {
      // 없으면 추가
      updatedEvent = [...currentEvent, newAlert];
    } else {
      // 있으면 교체(수정)
      updatedEvent = currentEvent.map((a, idx) =>
        idx === alertIndex ? { ...a, ...newAlert } : a
      );
    }

    return {
      ...item,
      [key]: updatedEvent,
    };
  });
};

const updateAssign = ({
  results,
  newAlert,
}: {
  results: DashboardFilterResult[];
  newAlert: AlertEntity;
}): DashboardFilterResult[] => {
  if (!results) return [];

  return results.map((item) =>
    item.recorderId !== newAlert.recorderId
      ? item
      : {
          ...item,
          alerts: item.alerts.map((alert) => ({
            ...alert,
            assigneeEmail: newAlert.assigneeEmail!,
            assigneeName: newAlert.assigneeName ?? "",
            assigneePhone: newAlert.assigneePhone ?? "",
            assigneeId: newAlert.assigneeId ?? "",
            notifySms: newAlert.notifySms,
          })),
        }
  );
};

// 현재 mapdata 에 assign 이벤트로 들어온 recorderId 가 없을 경우 체크하는 함수
const checkRecorderWhenAssigned = ({
  results,
  newAlert,
}: {
  results: DashboardFilterResult[];
  newAlert: AlertEntity;
}): boolean => {
  if (results.find((res) => res.recorderId === newAlert.recorderId)) {
    return true;
  }
  return false;
};

const stompMapSlice = createSlice({
  name: "stompMap",
  initialState,
  reducers: {
    setMapSearchOriginData: (
      state,
      action: PayloadAction<DashboardFilterResult[]>
    ) => {
      // console.log(action.payload);
      state.mapLastUpdatedAt = new Date();
      state.originFilterSearchResponse = [...action.payload];
      state.currentData = [...action.payload];
    },
    setMapBounds(state, action: PayloadAction<google.maps.LatLngBounds>) {
      const b = action.payload;
      state.mapBounds = {
        north: b.getNorthEast().lat(),
        east: b.getNorthEast().lng(),
        south: b.getSouthWest().lat(),
        west: b.getSouthWest().lng(),
      };
    },
    setSearchAsMoved(state, action: PayloadAction<boolean>) {
      state.searchAsMoved = action.payload;
    },

    mapViewEventUpdated: (state, action: PayloadAction<AlertServerPayload>) => {
      state.mapLastUpdatedAt = new Date();

      let tempCurrentData: DashboardFilterResult[] = [...state.currentData];
      const payload = action.payload;
      const status = payload.status.toLowerCase();

      // rule delete
      if (payload.event === "FORCE") {
        state.needToForceUpdated = new Date();
        return;
      }

      if (status !== "warnings") {
        state.mapLastUpdatedIdForAlertView = payload.recorderId;
      }

      const tempAlert: AlertEntity = makeAlertEntityByWs(payload);

      switch (payload.event as MapSocketMessageEventType) {
        case "DELETE": {
          if (payload.alertTypeInt === 999) {
            // console.log("all close alerts [] 빈배열로 만들기");
            tempCurrentData = allCloseAlerts({
              results: tempCurrentData,
              newAlert: tempAlert,
            });
            break;
          }

          tempCurrentData = closeEventByPayload({
            results: tempCurrentData,
            newAlert: tempAlert,
            status: WS_MAP_PAYLOAD_STATUS[status as StatusName],
          });
          break;
        }
        case "UPDATE": {
          // 해당 alert 에 assigneeEmail, assigneeName, assigneePhone 넣기
          if (status === "assigned" && tempAlert.assigneeEmail) {
            tempCurrentData = updateAssign({
              results: tempCurrentData,
              newAlert: tempAlert,
            });
            if (
              !checkRecorderWhenAssigned({
                results: tempCurrentData,
                newAlert: tempAlert,
              })
            ) {
              state.needToForceUpdated = new Date();
            }

            break;
          }
          break;
        }
        case "ADD": {
          tempCurrentData = addEventByPayload({
            results: tempCurrentData,
            newAlert: tempAlert,
            status: WS_MAP_PAYLOAD_STATUS[status as StatusName],
          });

          break;
        }
        // case "DEREGISTER": {
        //   tempCurrentData = deregisterRecorder({
        //     results: tempCurrentData,
        //     recorderId: tempAlert.recorderId,
        //   });
        //   break;
        // }
      }

      state.currentData = tempCurrentData;
    },
    mapViewRecorderAdded: (state, action) => {},
  },
});

export const mapSearchDataSelector = createSelector(
  (state: RootState) => state.mapStomp.currentData,
  (rows) => convertToMapSearchData(rows)
);

// 지도에 표시하는 data 는 location 이 있는 장비만 (missing address 제외)
export const markerDataSelector = createSelector(
  mapSearchDataSelector,
  (rows) => convertToMarkerGroupingData(rows.filter((row) => row.location))
);

// 왼쪽 검색결과에 뜨는 리스트들은 missing address 포함
export const visibleMapSearchDataSelector = createSelector(
  [
    mapSearchDataSelector,
    (s: RootState) => s.mapStomp.mapBounds,
    (s: RootState) => s.mapStomp.searchAsMoved,
  ],
  (mapSearchData, mapBounds, searchAsMoved) => {
    // console.log(mapSearchData);
    if (!mapSearchData || mapSearchData.length === 0) {
      return [];
    }
    if (!searchAsMoved || !mapBounds) {
      return mapSearchData;
    }

    const googleBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(mapBounds.south, mapBounds.west),
      new google.maps.LatLng(mapBounds.north, mapBounds.east)
    );

    return mapSearchData.filter((r) =>
      googleBounds.contains(new google.maps.LatLng(r.latitude, r.longitude))
    );
  }
);

export const {
  setMapSearchOriginData,
  setMapBounds,
  setSearchAsMoved,
  mapViewEventUpdated,
  mapViewRecorderAdded,
} = stompMapSlice.actions;
export const stompMapReducer = stompMapSlice.reducer;
